import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search, RotateCcw, RefreshCw, FileSpreadsheet, LayoutGrid, List, Users, Info,
  ChevronUp, ChevronDown, ChevronsUpDown,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import Breadcrumb, { BREADCRUMB_EXPORT_BTN } from '../../components/Breadcrumb/Breadcrumb';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import {
  fetchFilterAttendanceDetails,
  fetchFilterAttendanceDaily,
  resetFilterAttendance,
} from '../../store/slices/filterAttendanceSlice';
import { fetchAllHolidays } from '../../store/slices/holidaySlice';
import { exportAttendanceSummaryToExcel } from '../../utils/attendanceSummaryExcel';

import { createPortal } from 'react-dom';

/* Raw decimal hours */
const fmtH = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number(n.toFixed(2))} h`;
};

const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const COLUMNS = [
  { key: 'ServiceNo', label: 'Service No', align: 'center', width: 100, sticky: true, left: 0, className: 'text-gray-400 tabular-nums' },
  { key: 'Name', label: 'Name', align: 'left', width: 300, sticky: true, left: 100, className: 'text-gray-900 font-medium' },
  {
    key: 'CompanyWorkingDays',
    label: 'Company Working Days',
    align: 'center',
    width: 140,
    className: 'text-gray-600 tabular-nums',
    info: (
      <>
        <span className="font-semibold">Company Working Days</span> — total working days in the
        selected month, excluding Sundays and public / Poya / Mercantile holidays.
      </>
    ),
  },
  {
    key: 'AttendanceDays',
    label: 'Attendance Days (All)',
    align: 'center',
    width: 120,
    className: 'text-gray-600 tabular-nums',
    info: (
      <>
        <span className="font-semibold">Attendance Days (All)</span> = Working Days (Week) + Off Days.
      </>
    ),
  },
  {
    key: 'WorkingDays',
    label: 'Working Days (Week)',
    align: 'center',
    width: 130,
    className: 'text-gray-700 font-semibold tabular-nums',
    info: (
      <>
        <span className="font-semibold">Working Days (Week)</span> — days attended on Mon–Sat
        (non-holiday).
      </>
    ),
  },
  {
    key: 'OffDays',
    label: 'Off Days',
    align: 'center',
    width: 110,
    className: 'text-gray-700 font-semibold tabular-nums',
    info: (
      <>
        <span className="font-semibold">Off Days</span> — days attended on Sunday / Poya / Public /
        Mercantile holiday.
      </>
    ),
  },
  {
    key: 'ExtraDays',
    label: 'Extra Days',
    align: 'center',
    width: 110,
    info: (
      <>
        <span className="font-semibold">Extra Days</span> = Attendance Days (All) − Company Working
        Days. Shows days worked beyond the company's working days.
      </>
    ),
    render: (r) => r.ExtraDays > 0
      ? <span className="tabular-nums font-semibold text-blue-600">{r.ExtraDays}</span>
      : <span className="text-slate-400">—</span>,
  },
  {
    key: 'TotalHours',
    label: 'Total Hours',
    align: 'center',
    width: 120,
    info: (
      <>
        <span className="font-semibold">Total Hours</span> = Normal Hours + Adjusted OT Hours.
      </>
    ),
    render: (r) => (
      <span className="tabular-nums">{fmtH(r.TotalHours)}</span>
    ),
  },
  {
    key: 'NormalHours',
    label: 'Normal Hours',
    align: 'center',
    width: 140,
    info: (
      <>
        <span className="font-semibold">Normal Hours</span> — regular working hours inside the
        standard shift. Shown as decimal hours.
      </>
    ),
    render: (r) => (
      <span className="tabular-nums">{fmtH(r.NormalHours)}</span>
    ),
  },
  {
    key: 'OTHours',
    label: 'OT Hours',
    align: 'center',
    width: 120,
    info: (
      <>
        <span className="font-semibold">OT Hours</span> — total overtime hours.
      </>
    ),
    render: (r) => r.OTHours > 0
      ? <span className="tabular-nums font-semibold text-emerald-700">{fmtH(r.OTHours)}</span>
      : <span className="text-slate-400">—</span>,
  },
  { key: 'Designation', label: 'Designation', align: 'left', width: 150, className: 'text-gray-600' },
  { key: 'Department', label: 'Department', align: 'left', width: 150, className: 'text-gray-600' },
  { key: 'EmploymentCategory', label: 'Category', align: 'left', width: 150, className: 'text-gray-600' },
  { key: 'Location', label: 'Location', align: 'left', width: 180, className: 'text-gray-600' },
];


const STICKY_TOP = -24;
const STICKY_BOTTOM = -22;

const TABLE_MIN_WIDTH = COLUMNS.reduce((s, c) => s + c.width, 0);

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };

const today = new Date();
const DEFAULT_YEAR = today.getFullYear().toString();
const DEFAULT_MONTH = String(today.getMonth() + 1).padStart(2, '0');

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [];
for (let y = currentYear + 1; y >= currentYear - 5; y--) YEAR_OPTIONS.push(y.toString());

const MONTH_OPTIONS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

function parseAnyDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return { year: +iso[1], month: +iso[2], day: +iso[3] };
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return { year: +slash[3], month: +slash[1], day: +slash[2] };
  const d = new Date(s);
  if (!isNaN(d.getTime())) return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  return null;
}

/* helper for the info tooltips in the table header */
function InfoTip({ children }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleEnter = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ top: rect.bottom + 8, left: rect.right });
    setOpen(true);
  };
  const handleLeave = () => setOpen(false);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex items-center"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Info size={12} className="text-slate-400 hover:text-slate-600 cursor-help" />
      </span>
      {open &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[9999] w-64 -translate-x-full
                       rounded-lg bg-slate-900 text-white text-[11px] leading-snug font-normal
                       px-3 py-2 shadow-lg normal-case tracking-normal text-left"
            style={{ top: coords.top, left: coords.right ?? coords.left }}
          >
            {children}
            <span className="absolute right-3 -top-1 w-2 h-2 bg-slate-900 rotate-45" />
          </span>,
          document.body
        )}
    </>
  );
}

export default function AttendanceSummary() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { rows, dailyRows, loading, error } = useSelector((s) => s.filterAttendance);
  const holidays = useSelector((s) => s.holiday?.holidays || []);

  const isSummaryTab =
    location.pathname === '/hrm/attendance/details' ||
    location.pathname === '/hrm/attendance/summary';
  const isDetailsTab = location.pathname === '/hrm/attendance/attendance-details';
  const isOverviewTab = location.pathname === '/hrm/attendance/attendance-overview';

  const [year, setYear] = useState(DEFAULT_YEAR);
  const [month, setMonth] = useState(DEFAULT_MONTH);

  const [deptFilter, setDeptFilter] = useState('All');
  const [locFilter, setLocFilter] = useState('All');
  const [desigFilter, setDesigFilter] = useState('All');
  const [catFilter, setCatFilter] = useState('All');

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [sort, setSort] = useState({ key: 'Name', dir: 'asc' });
  const [refreshing, setRefreshing] = useState(false);


  const tableScrollRef = useRef(null);
  const barRef = useRef(null);
  const headerRef = useRef(null);
  const syncing = useRef(false);
  const [bar, setBar] = useState({ scrollWidth: 0, hasOverflow: false });

  const measureBar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const next = {
      scrollWidth: el.scrollWidth,
      hasOverflow: el.scrollWidth > el.clientWidth + 1,
    };
    setBar((prev) =>
      prev.scrollWidth === next.scrollWidth && prev.hasOverflow === next.hasOverflow ? prev : next
    );
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    measureBar();
    const ro = new ResizeObserver(measureBar);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    window.addEventListener('resize', measureBar);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureBar);
    };
  }, [measureBar]);

  const onTableScroll = () => {
    if (headerRef.current && tableScrollRef.current) {
      headerRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
    if (syncing.current) { syncing.current = false; return; }
    if (barRef.current && tableScrollRef.current) {
      syncing.current = true;
      barRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };
  const onBarScroll = () => {
    if (syncing.current) { syncing.current = false; return; }
    if (barRef.current && tableScrollRef.current) {
      syncing.current = true;
      tableScrollRef.current.scrollLeft = barRef.current.scrollLeft;
    }
  };

  const loadData = () => {
    dispatch(fetchFilterAttendanceDetails({ year, month }));
    dispatch(fetchFilterAttendanceDaily({ year, month }));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, dispatch]);

  useEffect(() => {
    dispatch(fetchAllHolidays());
  }, [dispatch]);

  const monthCalendar = useMemo(() => {
    const y = Number(year);
    const m = Number(month);
    const daysInMonth = new Date(y, m, 0).getDate();

    const offDateNumbers = new Set();

    for (let day = 1; day <= daysInMonth; day += 1) {
      const d = new Date(y, m - 1, day);
      if (d.getDay() === 0) offDateNumbers.add(day);
    }

    holidays.forEach((h) => {
      if (String(h?.HAH_IS_ACTIVE).toLowerCase() !== 'true') return;
      const p = parseAnyDate(h?.HAH_HOLIDAY_DATE);
      if (!p) return;
      if (p.year !== y || p.month !== m) return;
      offDateNumbers.add(p.day);
    });

    const offDays = offDateNumbers.size;
    const workingDays = daysInMonth - offDays;

    return { daysInMonth, workingDays, offDays, offDateNumbers };
  }, [year, month, holidays]);

  const attendanceByEmployee = useMemo(() => {
    const y = Number(year);
    const m = Number(month);
    const map = new Map();

    (dailyRows || []).forEach((d) => {
      const empId = String(d.EmployeeID ?? d.ServiceNo ?? '');
      if (!empId) return;

      const parsed = parseAnyDate(d.AttDate);
      if (!parsed) return;
      if (parsed.year !== y || parsed.month !== m) return;

      const jsDate = new Date(parsed.year, parsed.month - 1, parsed.day);

      if (!map.has(empId)) map.set(empId, new Map());
      const inner = map.get(empId);
      if (!inner.has(parsed.day)) {
        inner.set(parsed.day, { ...parsed, weekday: jsDate.getDay() });
      }
    });

    return map;
  }, [dailyRows, year, month]);

  const handleSearch = () => loadData();

  const handleReset = () => {
    setYear(DEFAULT_YEAR);
    setMonth(DEFAULT_MONTH);
    setDeptFilter('All');
    setLocFilter('All');
    setDesigFilter('All');
    setCatFilter('All');
    setSelectedEmployee(null);
    setSort({ key: 'Name', dir: 'asc' });
    dispatch(resetFilterAttendance());
  };

  const handleRefresh = useCallback(async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchFilterAttendanceDetails({ year, month })).unwrap(),
        dispatch(fetchFilterAttendanceDaily({ year, month })).unwrap(),
        dispatch(fetchAllHolidays()).unwrap(),
      ]);
    } catch {

    }
    finally { setRefreshing(false); }
  }, [dispatch, refreshing, loading, year, month]);

  const handleTabChange = (tab) => {
    if (tab === 'summary') navigate('/hrm/attendance/details');
    else if (tab === 'details') navigate('/hrm/attendance/attendance-details');
    else if (tab === 'overview') navigate('/hrm/attendance/attendance-overview');
  };

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const deptOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.Department).filter(Boolean))).sort(),
    [rows]
  );
  const locOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.Location).filter(Boolean))).sort(),
    [rows]
  );
  const desigOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.Designation).filter(Boolean))).sort(),
    [rows]
  );
  const catOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.EmploymentCategory).filter(Boolean))).sort(),
    [rows]
  );

  const employeeOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const id = String(r.EmployeeID ?? r.ServiceNo ?? '');
      if (!id || map.has(id)) return;
      map.set(id, {
        employeeId: id,
        serviceNo: r.ServiceNo || '',
        name: r.Name || '',
        department: r.Department || '',
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.serviceNo || '').localeCompare(b.serviceNo || '')
    );
  }, [rows]);

  const filtered = useMemo(() => {
    const empId =
      selectedEmployee && !selectedEmployee.isAll
        ? String(selectedEmployee.employeeId)
        : null;

    return rows.filter((r) => {
      if (deptFilter !== 'All' && r.Department !== deptFilter) return false;
      if (locFilter !== 'All' && r.Location !== locFilter) return false;
      if (desigFilter !== 'All' && r.Designation !== desigFilter) return false;
      if (catFilter !== 'All' && r.EmploymentCategory !== catFilter) return false;

      if (empId) {
        const rowId = String(r.EmployeeID ?? r.ServiceNo ?? '');
        if (rowId !== empId) return false;
      }
      return true;
    });
  }, [rows, deptFilter, locFilter, desigFilter, catFilter, selectedEmployee]);

  const enriched = useMemo(() => {
    const { workingDays: companyWorkingDays, offDateNumbers } = monthCalendar;

    return filtered.map((r) => {
      const empId = String(r.EmployeeID ?? r.ServiceNo ?? '');
      const dayMap = attendanceByEmployee.get(empId);

      let empWorkingDays;
      let empOffDays;

      if (dayMap && dayMap.size > 0) {
        empWorkingDays = 0;
        empOffDays = 0;

        dayMap.forEach((info) => {
          if (info.weekday === 0 || offDateNumbers.has(info.day)) {
            empOffDays += 1;
          } else {
            empWorkingDays += 1;
          }
        });
      } else {
        const fallbackDays = Number(r.AttendanceDays ?? 0);
        empWorkingDays = Math.min(fallbackDays, companyWorkingDays);
        empOffDays = Math.max(0, fallbackDays - companyWorkingDays);
      }

      const attendanceDays = empWorkingDays + empOffDays;

      return {
        ...r,
        CompanyWorkingDays: companyWorkingDays,
        AttendanceDays: attendanceDays,
        WorkingDays: empWorkingDays,
        OffDays: empOffDays,
      };
    });
  }, [filtered, monthCalendar, attendanceByEmployee]);

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    const list = [...enriched];
    list.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
      return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
    });
    return list;
  }, [enriched, sort]);


  const handleExport = () => {
    exportAttendanceSummaryToExcel({
      rows: sorted,
      filters: {
        Year: year,
        Month: month,
        Employee:
          selectedEmployee && !selectedEmployee.isAll
            ? `${selectedEmployee.serviceNo || ''} — ${selectedEmployee.name || ''}`.trim()
            : 'All Employees',
        Department: deptFilter,
        Location: locFilter,
        Designation: desigFilter,
        Category: catFilter,
      },
    });
  };

  const stickyStyle = (col) =>
    col.sticky ? { position: 'sticky', left: col.left } : undefined;

  return (
    <div className="p-0 space-y-2">
      <Breadcrumb
        trail={['eHRM', 'Attendance & Shifts', 'Attendance Summary']}
        title="Attendance Summary"
        action={
          <button onClick={handleExport} disabled={sorted.length === 0} className={BREADCRUMB_EXPORT_BTN}>
            <FileSpreadsheet size={15} />
            Export to Excel
          </button>
        }
      />

      <div className="border-b border-gray-200 bg-white rounded-t-xl px-4 pt-2">
        <nav className="flex gap-0" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('summary')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isSummaryTab
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <LayoutGrid size={16} />
            Attendance Summary
          </button>
          <button
            onClick={() => handleTabChange('details')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isDetailsTab
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <List size={16} />
            Attendance Details
          </button>
          <button
            onClick={() => handleTabChange('overview')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isOverviewTab
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
          >
            <Users size={16} /> Attendance Overview
          </button>
        </nav>
      </div>

      {/* ───────────────────────── FILTERS ───────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm space-y-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <span className={LABEL}>Year</span>
            <select value={year} onChange={(e) => setYear(e.target.value)} className={FIELD}>
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <span className={LABEL}>Month</span>
            <select value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD}>
              {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <span className={LABEL}>Department</span>
            <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className={FIELD}>
              <option value="All">All</option>
              {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <span className={LABEL}>Location</span>
            <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)} className={FIELD}>
              <option value="All">All</option>
              {locOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <span className={LABEL}>Designation</span>
            <select value={desigFilter} onChange={(e) => setDesigFilter(e.target.value)} className={FIELD}>
              <option value="All">All</option>
              {desigOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <span className={LABEL}>Category</span>
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={FIELD}>
              <option value="All">All</option>
              {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="pt-3 border-t border-gray-100">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="w-full md:w-96">
              <span className={LABEL}>Employee</span>
              <EmployeeSearch
                value={selectedEmployee}
                onSelect={setSelectedEmployee}
                employees={employeeOptions}
                loading={loading}
                showAllOption
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                <Search size={14} />
                {loading ? 'Loading...' : 'Search'}
              </button>
              <button
                onClick={handleReset}
                className="flex items-center gap-2 h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors"
              >
                <RotateCcw size={14} />
                Reset
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                title="Refresh data"
                className="flex items-center gap-2 h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ───────────────────────── END FILTERS ───────────────────────── */}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {/* ───────────────────────── TABLE ───────────────────────── */}
      <div className="mt-4 bg-white border border-gray-100 rounded-xl shadow-sm">
        {/* Card header */}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-800">Attendance Summary Records</span>
            <span className="text-xs text-slate-400 font-normal">
              {sorted.length} of {rows.length} records
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">

          </div>
        </div>

        <div
          ref={headerRef}
          className="sticky z-30 overflow-hidden bg-gray-50"
          style={{ top: STICKY_TOP }}
        >
          <table
            className="border-separate border-spacing-0 text-xs"
            style={{ width: '100%', minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}
          >
            <colgroup>
              {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
            </colgroup>

            <thead>
              <tr>
                {COLUMNS.map((c) => {
                  const active = sort.key === c.key;
                  return (
                    <th
                      key={c.key}
                      style={stickyStyle(c)}
                      className={`${c.sticky ? 'sticky z-20' : ''} bg-gray-50 border-b border-gray-200 px-3 py-2.5
              text-[12px] font-semibold text-gray-600 select-none ${ALIGN[c.align]}`}
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <span className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleSort(c.key)}
                          className={`inline-flex items-center gap-1 hover:text-gray-900 ${c.align === 'right' ? 'flex-row-reverse' : ''
                            }`}
                        >
                          <span>{c.label}</span>
                        </button>
                        {c.info && <InfoTip>{c.info}</InfoTip>}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>

        <div
          ref={tableScrollRef}
          onScroll={onTableScroll}
          className="relative overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          <table
            className="border-separate border-spacing-0 text-xs"
            style={{ width: '100%', minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}
          >
            <colgroup>
              {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
            </colgroup>

            <tbody>
              {loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-sm text-slate-400">
                    Loading attendance summary details...
                  </td>
                </tr>
              )}

              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-sm text-slate-400">
                    No attendance summary records found
                  </td>
                </tr>
              )}

              {sorted.map((r, i) => (
                <tr key={r.EmployeeID ?? r.ServiceNo ?? i} className="group">
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      style={stickyStyle(c)}
                      className={`${c.sticky ? 'sticky z-10' : ''} bg-white group-hover:bg-gray-50
                                  border-b border-gray-100 px-3 py-2 truncate
                                  ${ALIGN[c.align]} ${c.className || ''}`}
                    >
                      {c.render ? c.render(r) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

          </table>
        </div>

        <div
          ref={barRef}
          onScroll={onBarScroll}
          className="sticky z-30 overflow-x-auto overflow-y-hidden bg-white/95 border-t border-gray-200 rounded-b-xl"
          style={{
            bottom: STICKY_BOTTOM,
            height: 16,
            display: bar.hasOverflow ? 'block' : 'none',
          }}
          aria-hidden="true"
        >
          <div style={{ width: bar.scrollWidth, height: 1 }} />
        </div>
      </div>
    </div>
  );
}